import { useEffect, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, ArrowLeft } from 'lucide-react';

import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { IconButton } from '@/components/ui/button';
import useImportScanStore from '@modules/importScan/hook/useImportScanStore';
import { useCreateJawsScan } from '@hooks/useJawsScan';
import { JawsScanCreate } from '@schema/jawsScan';
import importScanStore from '@modules/importScan/store/importScanStore';
import fixRotationStore from '@modules/fixRotation/store/fixRotationStore';

export function UploadScan() {
  const { lowerJawMesh, upperJawMesh } = useImportScanStore();
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();

  const { mutate, isPending, isError, error, isSuccess } = useCreateJawsScan();

  const [readyToUpload, setReadyToUpload] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState('Analyzing scans');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 1) Give React a tick to render the blank/loading screen
  useEffect(() => {
    const timer = window.setTimeout(() => setReadyToUpload(true), 100);
    return () => window.clearTimeout(timer);
  }, []);

  // 2) Once we're “ready”, yield to browser then do analyze → serialize → mutate
  useEffect(() => {
    if (!readyToUpload) return;

    let cancelled = false;
    const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

    async function runUpload() {
      try {
        if (!lowerJawMesh || !upperJawMesh || !patientId) {
          throw new Error('Missing meshes or patientId');
        }

        // Phase 1: Analyzing
        setLoadingPhase('Analyzing scans');
        await delay(50); // give the UI time to paint
        if (cancelled) return;
        const lowerEdges = await lowerJawMesh.calculateEdges();
        const upperEdges = await upperJawMesh.calculateEdges();

        // Phase 2: Serializing
        setLoadingPhase('Serializing mesh data');
        await delay(50);
        if (cancelled) return;
        const lowerBlob = await lowerJawMesh.toPly({ excludeAttributes: ['normal', 'uv'] });
        const upperBlob = await upperJawMesh.toPly({ excludeAttributes: ['normal', 'uv'] });

        // Phase 3: Uploading
        setLoadingPhase('Uploading scans to server');
        await delay(50);
        if (cancelled) return;
        const data: JawsScanCreate = {
          patientId,
          note: '',
          scanDate: new Date().toISOString(),
          lowerJawData: { teethEdges: lowerEdges },
          upperJawData: { teethEdges: upperEdges },
        };
        mutate({
          data,
          lowerJawModel: lowerBlob,
          upperJawModel: upperBlob,
        });
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(err instanceof Error ? err.message : 'An unknown error');
        }
      }
    }

    runUpload();

    return () => {
      cancelled = true;
    };
  }, [readyToUpload, lowerJawMesh, upperJawMesh, patientId, mutate]);

  // 3) On success, redirect in 1.1s
  useEffect(() => {
    if (isSuccess) {
      const t = window.setTimeout(() => {
        navigate('/', { replace: true });
        setTimeout(() => {
          importScanStore.getState().reset();
          fixRotationStore.getState().reset();
        }, 0);
      }, 1100);
      return () => window.clearTimeout(t);
    }
  }, [isSuccess, navigate]);

  // Show spinner during analysis/serialization or while upload is pending
  const showLoading = (readyToUpload || isPending) && !isSuccess && !isError && !errorMessage;

  let content: ReactNode = null;

  if (showLoading) {
    content = (
      <>
        <header className="flex flex-col gap-2 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.4em] text-primary/70">Uploading scans</p>
          <h2 className="text-3xl font-semibold tracking-tight">Please hold on while we process your files</h2>
        </header>
        <LoadingSpinner className="mt-10 text-white" size={80} glowIntensity="md" />
        <p className="text-sm text-white/70">{loadingPhase}…</p>
      </>
    );
  } else if (isSuccess) {
    content = (
      <>
        <header className="flex flex-col items-center gap-3 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
            <CheckCircle className="h-10 w-10" />
          </span>
          <h2 className="text-3xl font-semibold tracking-tight">Upload successful!</h2>
          <p className="text-sm text-white/70">Great news—your scans are on their way. You’ll be redirected shortly.</p>
        </header>
      </>
    );
  } else if (isError || errorMessage) {
    content = (
      <>
        <header className="flex flex-col items-center gap-3 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500/15 text-red-300">
            <XCircle className="h-10 w-10" />
          </span>
          <h2 className="text-3xl font-semibold tracking-tight">Upload failed</h2>
          <p className="text-sm text-white/70">
            {errorMessage ?? (error instanceof Error ? error.message : 'Failed to upload. Please try again.')}
          </p>
        </header>
        <IconButton
          className="mt-6"
          variant="ghost-light"
          leftIcon={ArrowLeft}
          onClick={() => navigate('/', { replace: true })}
        >
          Go back
        </IconButton>
      </>
    );
  }

  return (
    <section className="flex h-full w-full items-center justify-center p-6">
      <div className="flex w-full max-w-xl flex-col items-center gap-8 rounded-3xl border border-primary/40 bg-content/85 p-10 text-white shadow-2xl shadow-primary/25 backdrop-blur">
        {content}
      </div>
    </section>
  );
}
